import type {
  ContactAddress,
  FunnelAddress,
  FunnelChannelSession,
} from 'src/api/generated';
import type { AddressPairSelection } from '../../types/whatsapp-address-selection.types';
import { isSessionActive } from 'src/modules/shared/utils/expiration.util';

/**
 * Finds the best pair of addresses for WhatsApp messaging.
 *
 * Selection priority:
 * 1. A compatible pair with an active session
 * 2. The most recent compatible historical pair (for expired conversations)
 * 3. No pair when there is no compatible session history
 *
 * @param contactAddresses - Array of available contact addresses to check
 * @param funnelAddresses - Array of available funnel addresses to check
 * @param sessions - Array of channel sessions to search through
 * @returns Object containing the preferred pair found, or null values if none exist
 */
const findPreferredAddressPair = (
  contactAddresses: ContactAddress[],
  funnelAddresses: FunnelAddress[],
  sessions: FunnelChannelSession[]
): AddressPairSelection => {
  const noAddressesAvailable =
    !contactAddresses.length || !funnelAddresses.length;
  const noSessionsAvailable = !sessions.length;

  if (noAddressesAvailable || noSessionsAvailable) {
    return { contactId: null, funnelId: null };
  }

  const contactAddressIds = new Set(contactAddresses.map((addr) => addr.id));
  const funnelAddressIds = new Set(funnelAddresses.map((addr) => addr.id));
  let historicalPair: AddressPairSelection | null = null;

  for (const session of sessions) {
    const sessionContactAddressExists = contactAddressIds.has(
      session.contactAddressId
    );
    const sessionFunnelAddressExists = funnelAddressIds.has(
      session.funnelAddressId
    );
    const bothAddressesExist =
      sessionContactAddressExists && sessionFunnelAddressExists;

    if (!bothAddressesExist) {
      continue;
    }

    const pair = {
      contactId: session.contactAddressId,
      funnelId: session.funnelAddressId,
    };

    if (isSessionActive(session.endTime)) {
      return pair;
    }

    historicalPair ??= pair;
  }

  if (historicalPair) {
    return historicalPair;
  }

  return { contactId: null, funnelId: null };
};

/**
 * Determines initial address selections based on available addresses and session history.
 *
 * This function implements the selection priority logic for the initial state:
 * 1. Addresses from active session pairs (preferred)
 * 2. Addresses from the latest compatible historical pair
 * 3. Single addresses when only one option exists
 * 4. No selection when multiple options exist without session history
 *
 * @param contactAddresses - Array of available contact addresses
 * @param funnelAddresses - Array of available funnel addresses
 * @param sessions - Array of channel sessions for finding compatible pairs
 * @returns Object with initial contactId and funnelId selections
 *
 * @example
 * ```typescript
 * const initial = getInitialAddressSelections(contacts, funnels, sessions);
 * // Returns: { contactId: 123, funnelId: 456 } or nulls
 * ```
 */
export const getInitialAddressSelections = (
  contactAddresses: ContactAddress[],
  funnelAddresses: FunnelAddress[],
  sessions: FunnelChannelSession[]
) => {
  const preferredPair = findPreferredAddressPair(
    contactAddresses,
    funnelAddresses,
    sessions
  );

  const hasSingleContactAddress = contactAddresses.length === 1;
  const hasSingleFunnelAddress = funnelAddresses.length === 1;

  const contactId =
    preferredPair.contactId ??
    (hasSingleContactAddress ? contactAddresses[0].id : null);

  const funnelId =
    preferredPair.funnelId ??
    (hasSingleFunnelAddress ? funnelAddresses[0].id : null);

  return { contactId, funnelId };
};
